import { computed, inject } from "vue";
import { storeToRefs } from "pinia";
import type {
	ProductItem,
	SimplifiedProductRelations
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import { isBundleProductTypeName } from "~/utils/constants";

/**
 * Everything the PDP's attribute panel knows, so a template only has to draw
 * it. Every fork of `ProductAttributes.vue` carried its own copy of this —
 * which is how two of them drifted onto a narrower bundle check and one grew a
 * seventh copy of the add-to-cart stock rule.
 *
 * Markup stays per-template: this owns the reading, not the layout.
 */
export const useProductAttributes = (
	options: {
		/**
		 * Stock to judge the product by. Defaults to its own on-hand; a pack
		 * passes the stock derived from its structure, because Norce reports
		 * zero for the pack product itself.
		 */
		stock?: () => number;
		/**
		 * Runs before a side panel opens — the PDP modal closes itself first,
		 * so the panel is not stacked on top of it.
		 */
		onClose?: () => void;
	} = {}
) => {
	const { getProductStock } = useNorcePwa();
	const { selectedProduct, relations } = storeToRefs(useProductStore());
	// The store types this loosely; every fork cast or ignored it in its own
	// copy. One site now, so the narrowing is stated once. Passed as a computed,
	// not the value: picking another variant replaces `selectedProduct`, and a
	// snapshot kept the first variant's old price (or none) for every other.
	const { oldPrice } = useProductPrices(
		computed(() => selectedProduct.value as ProductItem | undefined)
	);

	/*
		hogsta forks the product store and its copy carries neither of these, so
		they are read off a named optional surface rather than destructured —
		the same rule as `useDisplayBasket`: base code may assume only what
		every fork has. `TEMPLATE=default` would stay green either way.
	*/
	const bundleState = useProductStore() as unknown as {
		productType?: string | null;
		bundleProducts?: unknown[];
	};

	// Optional: templates that open the panel inline hold their own ref and
	// never provide one.
	const modal = inject<SharedModal | undefined>("modal", undefined);

	const productStock = computed(() =>
		selectedProduct.value ? getProductStock(selectedProduct.value) : 0
	);

	/** What "sold out" is measured against — see `options.stock`. */
	const resolvedStock = computed(() =>
		options.stock ? options.stock() : productStock.value
	);

	// Norce names the type `ManagedStructure` or `Bundle` depending on the
	// space, so the check is on the set, never on one literal.
	const isBundleProductType = computed(
		() =>
			isBundleProductTypeName(bundleState.productType) &&
			(bundleState.bundleProducts?.length ?? 0) > 0
	);

	const isOOS = computed(
		() => !!selectedProduct.value && resolvedStock.value === 0
	);

	const relationVariants = computed(() => {
		const rels = relations.value as SimplifiedProductRelations[] | undefined;
		return rels?.find((relation) =>
			relation.Code?.includes(ProductPrefix.RELATION_VARIANTS)
		);
	});

	const openStoreAvailability = () => {
		options.onClose?.();
		modal?.open("StoreAvailability", { position: "side" });
	};

	return {
		selectedProduct,
		relations,
		oldPrice,
		productStock,
		isBundleProductType,
		isOOS,
		relationVariants,
		openStoreAvailability
	};
};
